import firebaseConfig from "./firebaseServiceConfig";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import "firebase/compat/functions";
import id from "../friendly-id/id-gen";
import {
  Klydo,
  Version,
  Stats,
  KlydoTimes,
  User,
  Device,
  ScheduledTask,
  DeviceValue,
  General,
  CommandServer,
  Kpi,
  DeviceReport,
  PendingKlydo,
} from "Types";
import { DocumentData, WhereFilterOp } from "firebase/firestore";
import axios from "axios";
import JSZip from "jszip";

const dataRefreshTime = 300_000;

// Firing thousands of concurrent reads/writes at once overwhelms Firestore and
// many requests fail with HTTP 400. Process the devices in bounded chunks
// instead, and collect the ids that failed.
const DEVICE_CHUNK_SIZE = 200;

/** Devices finished so far in the current pass; `retrying` on the retry pass. */
export type DeviceRunProgress = {
  done: number;
  total: number;
  retrying: boolean;
};

const runOnDevicesInChunks = async (
  deviceIds: string[],
  run: (deviceId: string) => Promise<unknown>,
  onProgress?: (progress: DeviceRunProgress) => void,
): Promise<{ succeeded: string[]; failed: string[] }> => {
  const runPass = async (ids: string[], retrying: boolean) => {
    const failed: string[] = [];
    let done = 0;
    // Reported on each whole percent, not per device: one React render per
    // device would be 20k renders for a fleet-wide write.
    let lastPercent = -1;
    const report = () => {
      const percent = Math.floor((done * 100) / ids.length);
      if (percent === lastPercent) return;
      lastPercent = percent;
      onProgress?.({ done, total: ids.length, retrying });
    };
    report();
    for (let i = 0; i < ids.length; i += DEVICE_CHUNK_SIZE) {
      const chunk = ids.slice(i, i + DEVICE_CHUNK_SIZE);
      const results = await Promise.allSettled(
        chunk.map((id) =>
          run(id).finally(() => {
            done += 1;
            report();
          }),
        ),
      );
      results.forEach((res, idx) => {
        if (res.status === "rejected") failed.push(chunk[idx]);
      });
    }
    return failed;
  };

  let failed = await runPass(deviceIds, false);
  // One retry pass to recover transient failures (throttling, network).
  if (failed.length) failed = await runPass(failed, true);

  const failedSet = new Set(failed);
  const succeeded = deviceIds.filter((id) => !failedSet.has(id));
  return { succeeded, failed };
};
type DataType =
  | Device
  | DeviceReport
  | Stats
  | Version
  | CommandServer
  | ScheduledTask
  | User
  | General;
type Listener<T> = (dt: T[]) => void;
class DataHandler<T extends DataType> {
  dependencies: Collection[];
  data?: T[];
  parser: (data: any) => T;
  listeners: Listener<T>[] = [];
  getter: (dh: DataHandler<T>, data: DataType[][]) => Promise<T[]>;
  key: string;
  lastDownloadedData = 0;
  trigger() {
    if (this.data) {
      for (const l of this.listeners) {
        l(this.data);
      }
    }
  }

  async load() {
    if (new Date().getTime() - this.lastDownloadedData < dataRefreshTime) {
      if (this.data) return;
      return new Promise<void>((v, x) => {
        const inv = setInterval(() => {
          if (this.data) {
            clearInterval(inv);
            v();
          }
        }, 500);
      });
    }
    this.lastDownloadedData = new Date().getTime();
    await Promise.all(this.dependencies.map((k) => instance.data[k].load()));
    this.data = await this.getter(
      this,
      this.dependencies.map((k) => instance.data[k].data!),
    );
    for (const l of this.listeners) {
      l(this.data);
    }
  }
  constructor(
    key: Collection,
    getter: (dh: DataHandler<T>, data: DataType[][]) => Promise<T[]>,
    parser: (data: any) => T,
    dependencies: Collection[] = [],
  ) {
    this.key = key;
    this.getter = getter;
    this.dependencies = dependencies;
    this.parser = parser;
    setTimeout(
      () =>
        setInterval(() => {
          if (this.listeners.length) this.load();
        }, dataRefreshTime),
      dataRefreshTime,
    );
  }
}
type Collection =
  | "machinesReport"
  | "version"
  | "klydos"
  | "stats"
  | "reviews"
  | "users";
class FirebaseService {
  db: firebase.firestore.Firestore;
  private storage: firebase.storage.Storage;
  private functions: firebase.functions.Functions;
  private auth: firebase.auth.Auth;
  data = {
    machinesReport: new DataHandler<DeviceReport>(
      "machinesReport",
      async (dh: DataHandler<DeviceReport>) => {
        const data = await this.db.collection("machinesReport").get();
        const machinesReport: DeviceReport[] = data.docs.map((d) => {
          const data = d.data();
          data.id = d.id;
          return dh.parser(data);
        });
        return machinesReport;
      },
      (data) => ({
        ...data,
        id: data.id,
        heartbeat: this.toDate(data.heartbeat),
        clocktime: data.clocktime
          ? {
              local: data.clocktime.local,
              server: this.toDate(data.clocktime.server),
              timeZone: data.clocktime.timeZone,
            }
          : undefined,
      }),
    ),
    version: new DataHandler<Version>(
      "version",
      async (dh: DataHandler<Version>) => {
        const data = await this.db.collection("version").get();
        const versions = data.docs.map((raw) => {
          return dh.parser({ id: raw.id, ...raw.data() });
        });
        return versions;
      },
      (data: any) => ({ ...data, date: this.toDate(data.upload_date) }),
    ),
    klydos: new DataHandler<Klydo>(
      "klydos",
      async (dh: DataHandler<Klydo>, dependencies: DataType[][]) => {
        const data = await this.db.collection("klydos").get();
        const klydos: Klydo[] = data.docs.map((i) => {
          const raw = i.data();
          raw.id = i.id;
          return dh.parser(raw);
        });
        klydos.forEach((kld) => {
          const stat = (dependencies[0] as Stats[]).find(
            (s) => s.id === kld.id,
          );
          kld.stats = {
            time: stat?.time || 0,
            favorites: stat?.favs || 0,
            viewed: 0,
          };
        });
        return klydos;
      },
      (data: any) => ({
        ...data,
        idf: id.friendlyFromHex(data.id),
        createdAt: this.toDate(data.createdAt),
        updatedAt: this.toDate(data.updatedAt),
        tags: data.tags ?? [],
        featured: data.featured,
        featuredTimes: {
          start: data.featuredTimes?.start
            ? this.toDate(data.featuredTimes.start)
            : undefined,
          end: data.featuredTimes?.end
            ? this.toDate(data.featuredTimes.end)
            : undefined,
        },
        inTask: !!data.inTasks,
        author: data.authorName,
        unlisted: !!data.unlisted,
      }),
      ["stats"],
    ),
    stats: new DataHandler<Stats>(
      "stats",
      async (dh: DataHandler<Stats>) => {
        const data = await this.db.collection("stats").get();
        return data.docs.map((i) => {
          const d = i.data();
          d.id = i.id;
          return dh.parser(d);
        });
      },
      (data: any) => ({
        ...data,
      }),
    ),
    reviews: new DataHandler<Klydo>(
      "reviews",
      async (dh: DataHandler<Klydo>) => {
        const data = await this.db
          .collectionGroup("my_klydos")
          .orderBy("review")
          .get();
        const ids: string[] = [];
        data.docs.forEach((i) => {
          const id = i.ref.parent.parent!.id;
          if (!ids.includes(id)) {
            ids.push(id);
          }
        });
        let usrs: { uid: string; displayName: string }[];
        if (this.data.users.data) {
          usrs = this.data.users.data;
        } else {
          const users = await this.db
            .collection("users")
            .where(firebase.firestore.FieldPath.documentId(), "in", ids)
            .get();
          usrs = users.docs.map((i) => ({
            uid: i.id,
            displayName: i.get("name"),
          }));
        }
        const reviews = data.docs.map((i) => {
          const raw = i.data();
          raw.id = i.id;
          raw.creator = i.ref.parent.parent!.id;
          raw.author = usrs.find(
            (u) => u.uid === i.ref.parent.parent!.id,
          )?.displayName;
          return dh.parser(raw);
        });
        return reviews;
      },
      (data: any) => {
        if (data.review?.date) {
          data.review.date = this.toDate(data.review.date);
        }
        const klydo = {
          ...data,
          idf: id.friendlyFromHex(data.id),
          createdAt: this.toDate(data.createdAt),
          updatedAt: this.toDate(data.updatedAt),
        };
        return klydo;
      },
    ),
    users: new DataHandler<User>(
      "users",
      async (dh: DataHandler<User>) => {
        const data = await Promise.all([
          this.functions.httpsCallable("getUsers2")(),
          this.db.collection("userBasicInfo").get(),
        ]);
        const users: User[] = data[0].data;
        users.forEach((u: any) => {
          u.created = new Date(u.creationTime);
          u.used = new Date(u.lastRefreshTime);
        });
        data[1].docs.forEach((ii) => {
          const f = users.find((i) => i.uid === ii.id);
          if (f) f.basicUserInfo = ii.data() as { tag: string };
        });
        return users;
      },
      (data: any) => ({
        ...data,
      }),
    ),
  };
  listen<T extends DataType>(key: Collection, listener: Listener<T>) {
    const dh = this.data[key] as unknown as DataHandler<T>;
    dh.listeners.push(listener);
    if (dh.data) {
      listener(dh.data);
    }
    dh.load();
  }
  removeListener(key: Collection, listener: Listener<any>) {
    const dh = this.data[key];
    dh.listeners.splice(dh.listeners.indexOf(listener), 1);
  }
  updateCachedItem(key: Collection, id: string, updates: Partial<DataType>) {
    const dh = this.data[key];
    if (!dh.data) return;
    const arr = dh.data as Array<DataType & { id?: string }>;
    const item = arr.find((d) => d.id === id);
    if (item) {
      Object.assign(item, updates);
      dh.trigger();
    }
  }
  general?: General;

  constructor() {
    firebase.initializeApp(firebaseConfig);
    this.db = firebase.firestore();
    this.functions = firebase.app().functions("us-central1");
    this.storage = firebase.storage();
    this.auth = firebase.auth();
    this.db
      .collection("general")
      .doc("config")
      .onSnapshot((c) => {
        this.general = c.data() as General;
      });
  }
  kpiListeners: { callB: (kpis: Kpi) => void; unsubscribe: () => void }[] = [];
  ordersListeners: {
    callB: (orderDates: Date[]) => void;
    unsubscribe: () => void;
  }[] = [];
  async getKlydoFrames(
    klydoLoopId: string,
    callBack: (frames: { url: string; name: string }[]) => void,
    progressCallback?: (progress: number) => void,
  ) {
    const file = await this.storage.ref(`${klydoLoopId}.zip`).getDownloadURL();
    const response = await axios
      .get(file, {
        responseType: "arraybuffer",
        onDownloadProgress: (progressEvent) => {
          if (progressCallback && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            progressCallback(percentCompleted);
          }
        },
      })
      .then((res) => res.data);
    const zip = await JSZip.loadAsync(response);
    const imagePromises: Promise<{ url: string; name: string }>[] = [];
    zip.forEach((relativePath, file) => {
      if (!file.dir && /\.(jpe?g|png|gif|webp)$/i.test(relativePath)) {
        imagePromises.push(
          file.async("blob").then((blob) => ({
            url: URL.createObjectURL(blob),
            name: relativePath,
          })),
        );
      }
    });
    const imageUrls = await Promise.all(imagePromises);
    callBack(imageUrls);
  }
  listenKpis(cb: (kpis: Kpi) => void) {
    this.kpiListeners.push({
      callB: cb,
      unsubscribe: this.db
        .collection("general")
        .doc("kpis")
        .onSnapshot((c) => cb(c.data() as Kpi)),
    });
  }
  stopListenKpis(cb: (kpis: Kpi) => void) {
    const lst = this.kpiListeners.find((listener) => listener.callB === cb);
    if (lst) {
      lst.unsubscribe();
      this.kpiListeners.splice(this.kpiListeners.indexOf(lst), 1);
    }
  }
  listenOrders(cb: (orderDates: Date[]) => void) {
    this.ordersListeners.push({
      callB: cb,
      unsubscribe: this.db
        .collection("general")
        .doc("order_dates")
        .onSnapshot((c) =>
          cb(
            c
              .data()
              ?.dates.map((date: { seconds: number; nanoseconds: number }) =>
                this.toDate(date),
              ),
          ),
        ),
    });
  }
  stopListenOrders(cb: (orderDates: Date[]) => void) {
    const lst = this.ordersListeners.find((listener) => listener.callB === cb);
    if (lst) {
      lst.unsubscribe();
      this.ordersListeners.splice(this.ordersListeners.indexOf(lst), 1);
    }
  }

  genFbID() {
    return this.db.collection("a").doc().id;
  }
  onAuthStateChanged(callback: any) {
    if (!this.auth) {
      return;
    }
    this.auth.onAuthStateChanged(callback);
  }
  getUserName() {
    return this.auth.currentUser?.displayName || this.auth.currentUser?.email;
  }
  async logOut() {
    if (!this.auth) {
      return;
    }
    this.auth.signOut();
  }
  toDate(o: any) {
    if (typeof o == "number") return new Date(o);
    if (!o || !o.seconds) return new Date(0);
    return new Date(o.seconds * 1000);
  }

  loginWithEmail(user: { email: string; password: string }) {
    const { email, password } = user;
    return new Promise((resolve, reject) => {
      this.auth &&
        this.auth
          .signInWithEmailAndPassword(email, password)
          .then((response) => {
            resolve(response);
            // this.getUserDocument(response.user.uid).then((user) => {
            //     resolve(user);
            // }).catch(err => {
            //     reject(err)
            // })
          })
          .catch((error) => {
            const usernameErrorCodes = [
              "auth/email-already-in-use",
              "auth/invalid-email",
              "auth/operation-not-allowed",
              "auth/user-not-found",
              "auth/user-disabled",
            ];
            const passwordErrorCodes = [
              "auth/weak-password",
              "auth/wrong-password",
            ];

            const response = {
              email: usernameErrorCodes.includes(error.code)
                ? error.message
                : "",
              password: passwordErrorCodes.includes(error.code)
                ? error.message
                : "",
            };
            reject(response);
          });
    });
  }

  loginSocial(provider: string) {
    return this.auth.signInWithPopup(
      provider === "facebook"
        ? new firebase.auth.FacebookAuthProvider()
        : new firebase.auth.GoogleAuthProvider(),
    );
  }

  async updateConfig(id: string, config: string, value: any) {
    await this.db
      .collection("machines")
      .doc(id)
      .update({
        [config]:
          value == null
            ? firebase.firestore.FieldValue.delete()
            : {
                time: firebase.firestore.FieldValue.serverTimestamp(),
                value: value,
                source: "api",
              },
      });
  }

  reomveRegister(id: string) {
    const clock = this.data.users.data?.find((u) => u.uid === id)?.email;
    return Promise.all([
      this.db.collection("machines").doc(id).update({
        registerProduct: firebase.firestore.FieldValue.delete(),
        emailSent: firebase.firestore.FieldValue.delete(),
      }),
      this.db.collection("register").doc(clock?.split("@")[0]).delete(),
    ]);
  }

  public getToken() {
    return this.auth.currentUser?.getIdToken();
  }

  public getUserId() {
    return this.auth.currentUser?.uid;
  }

  registerDevice(
    device: Device,
    clientDetails?: {
      country?: string;
      state?: string;
      city?: string;
      name?: string;
      email?: string;
    },
  ) {
    const obj: {
      registerProduct: DeviceValue<true>;
      clientDetails?: {
        country?: string;
        state?: string;
        city?: string;
        name?: string;
        email?: string;
      };
    } = {
      registerProduct: { time: new Date(), value: true },
    };
    if (clientDetails) {
      obj.clientDetails = clientDetails;
    }

    return this.db.collection("machines").doc(device.id).update(obj);
  }

  sendComandToClock(id: string, command: string, param: string) {
    return new Promise<void>((v, x) => {
      this.db
        .collection("machines")
        .doc(id)
        .update({ command: { c: command, p: param } })
        .then((_) => {
          const unreg = this.db
            .collection("machines")
            .doc(id)
            .onSnapshot((doc) => {
              if (!doc.data()?.command) {
                unreg();
                v();
              }
            });
        })
        .catch(x);
    });
  }

  async resetDeviceData(uid: string, idf: string) {
    await this.db.collection("machines").doc(uid).set({ idf: idf });
  }

  async updateField(id: string, field: string, value: any) {
    await this.db
      .collection("machines")
      .doc(id)
      .update({
        [field]:
          value === null ? firebase.firestore.FieldValue.delete() : value,
      });
  }

  updateUserAnimatedPhoto = async (url: string) => {
    try {
      this.functions.httpsCallable("splitGifBridge")({
        url: url,
        artist: true,
      });
    } catch (e) {
      console.log(e);
    }
  };

  removeAttachedClock(uid: string) {
    return this.functions.httpsCallable("disconnectClock")({ uid: uid });
  }

  async updateKlydoTimes(
    klydo: Klydo,
    timesArray: Array<KlydoTimes>,
    isReview: boolean = false,
  ) {
    const batch = this.db.batch();
    batch.set(
      this.db
        .collection("users")
        .doc(klydo.creator)
        .collection("my_klydos")
        .doc(klydo.id),
      { times: timesArray },
      { merge: true },
    );
    if (!isReview) {
      batch.set(
        this.db.collection("klydos").doc(klydo.id),
        { times: timesArray },
        { merge: true },
      );
    }
    await batch.commit();
    this.data.klydos.trigger();
  }

  async addPendingKlydosToDevices(
    deviceIds: string[],
    pendingKlydos: PendingKlydo[],
  ): Promise<{ succeeded: string[]; failed: string[] }> {
    const collectionRef = this.db.collection("machines");

    // Read-modify-write for a single device. Skips klydos already pending on
    // the device so re-running the operation never appends duplicates.
    const addToDevice = async (deviceId: string) => {
      const doc = await collectionRef.doc(deviceId).get();
      const existing: PendingKlydo[] = doc.data()?.pendingKlydos?.value ?? [];
      const existingIds = new Set(existing.map((k) => k.id));
      const toAdd = pendingKlydos.filter((k) => !existingIds.has(k.id));
      if (!toAdd.length) return;
      await collectionRef.doc(deviceId).update({
        pendingKlydos: {
          time: firebase.firestore.FieldValue.serverTimestamp(),
          value: [...existing, ...toAdd],
          source: "api",
        },
      });
    };

    return runOnDevicesInChunks(deviceIds, addToDevice);
  }

  /**
   * Write synced settings to many devices, one doc update per device with all
   * of its fields. `valuesFor` gets the device's version from a fresh read of
   * its doc (not the table row, which can be a stale pinned snapshot), so it can
   * skip a field that version doesn't run, or store it in that version's range.
   * Devices it returns no fields for are left untouched and listed in `skipped`.
   */
  async updateConfigsOnDevices(
    deviceIds: string[],
    valuesFor: (version?: number) => Record<string, unknown>,
    onProgress?: (progress: DeviceRunProgress) => void,
  ): Promise<{ succeeded: string[]; failed: string[]; skipped: string[] }> {
    const collectionRef = this.db.collection("machines");
    const skipped = new Set<string>();
    const updateDevice = async (deviceId: string) => {
      const doc = await collectionRef.doc(deviceId).get();
      const values = valuesFor(doc.data()?.version);
      if (!Object.keys(values).length) {
        skipped.add(deviceId);
        return;
      }
      await collectionRef.doc(deviceId).update(
        Object.fromEntries(
          Object.entries(values).map(([field, value]) => [
            field,
            {
              time: firebase.firestore.FieldValue.serverTimestamp(),
              value,
              source: "api",
            },
          ]),
        ),
      );
    };
    const { succeeded, failed } = await runOnDevicesInChunks(
      deviceIds,
      updateDevice,
      onProgress,
    );
    return {
      succeeded: succeeded.filter((id) => !skipped.has(id)),
      failed,
      skipped: Array.from(skipped),
    };
  }

  /**
   * Drop ONE klydo from a device's pending queue, read-modify-write against the
   * live doc.
   *
   * Not a whole-array write off whatever the page rendered: the clock rewrites
   * this same array from its own snapshot and the backend appends to it, so
   * filtering a stale copy and writing it back silently drops everything that
   * arrived since the render (a gift was lost in production that way). Removing
   * one id off a fresh read touches only that id.
   */
  async removePendingKlydoFromDevice(deviceId: string, klydoId: string) {
    const docRef = this.db.collection("machines").doc(deviceId);
    const doc = await docRef.get();
    const existing: PendingKlydo[] = doc.data()?.pendingKlydos?.value ?? [];
    if (!existing.some((entry) => entry.id === klydoId)) return;

    await docRef.update({
      pendingKlydos: {
        time: firebase.firestore.FieldValue.serverTimestamp(),
        value: existing.filter((entry) => entry.id !== klydoId),
        source: "api",
      },
    });
  }

  /**
   * Apply an add/remove diff to a device's hiddenKlydos list, read-modify-write
   * against the live doc.
   *
   * Not a whole-array write off whatever the page rendered: the clock writes
   * this same array itself every time the user deletes a klydo on the device,
   * so writing back a filtered copy of a render-time snapshot would un-hide
   * anything hidden since the page loaded. Applying a diff off a fresh read
   * touches only the ids the admin actually changed, so it is also safe to
   * retry.
   *
   * New entries carry `deletedAt: Date.now()` — the same shape and units the
   * clock stores (`System.currentTimeMillis()`).
   */
  async updateDeviceHiddenKlydos(
    deviceId: string,
    { add = [], remove = [] }: { add?: string[]; remove?: string[] },
  ) {
    const docRef = this.db.collection("machines").doc(deviceId);
    const doc = await docRef.get();
    const existing: Array<{ id: string; deletedAt: number }> =
      doc.data()?.hiddenKlydos?.value ?? [];

    const removeSet = new Set(remove);
    const existingIds = new Set(existing.map((entry) => entry.id));
    const kept = existing.filter((entry) => !removeSet.has(entry.id));
    const now = Date.now();
    const toAdd = add
      .filter((id) => !existingIds.has(id) && !removeSet.has(id))
      .map((id) => ({ id, deletedAt: now }));

    if (!toAdd.length && kept.length === existing.length) return;

    await docRef.update({
      hiddenKlydos: {
        time: firebase.firestore.FieldValue.serverTimestamp(),
        value: [...kept, ...toAdd],
        source: "api",
      },
    });
  }

  sendCommandToClocksByBatch(data: any) {
    const collectionRef = this.db.collection("machines");
    const obj: {
      name: string;
      local?: boolean;
      timeout?: number;
      time: Date;
      params?: Array<string>;
      expiresAt?: Date;
    } = {
      name: data.name,
      local: data.local,
      time: data.date,
    };
    if (!data.local) {
      delete obj.local;
    }
    if (data.params && data.params.length) obj.params = data.params;
    if (data.expire) obj.timeout = data.expire;
    // Without expiresAt, the klydo-api delete-old-commands cron deletes the
    // command after ~1 day. expiresAt keeps it until that time instead.
    if (data.expiresAt) obj.expiresAt = data.expiresAt;

    // Firestore caps a single WriteBatch at 500 operations, so split the
    // devices into chunks and commit one batch per chunk.
    const FIRESTORE_BATCH_LIMIT = 500;
    const devices: string[] = data.devices;
    const commits: Array<Promise<unknown>> = [];
    for (let i = 0; i < devices.length; i += FIRESTORE_BATCH_LIMIT) {
      const batch = this.db.batch();
      devices.slice(i, i + FIRESTORE_BATCH_LIMIT).forEach((deviceId) => {
        batch.set(
          collectionRef.doc(deviceId).collection("commands").doc(data.id),
          obj,
        );
      });
      commits.push(batch.commit());
    }

    return Promise.all(commits);
  }

  getKlydoAnalyticsByDevice(deviceId: string) {
    return new Promise<Array<any>>((v, x) => {
      this.functions
        .httpsCallable("queryKlydosAnalytics")({
          uid: deviceId,
        })
        .then((u) => {
          v(u.data);
        })
        .catch(x);
    });
  }

  private async buildDeviceFromDoc(
    machineDocId: string,
    machineData: firebase.firestore.DocumentData,
  ): Promise<Device> {
    const reportSnapshot = await this.db
      .collection("machinesReport")
      .doc(machineDocId)
      .get();

    const reportData = reportSnapshot.exists ? reportSnapshot.data() : null;

    return {
      ...machineData,
      id: machineDocId,
      registerProduct: machineData.registerProduct
        ? this.toDate(machineData.registerProduct.time)
        : undefined,
      autoCreate: machineData.autoCreate ?? false,
      heartbeat: reportData?.heartbeat || machineData.heartbeat,
      clocktime:
        reportData?.clocktime || machineData.clocktime
          ? {
              local: (reportData?.clocktime || machineData.clocktime).local,
              server: this.toDate(
                (reportData?.clocktime || machineData.clocktime).server,
              ),
              timeZone: (reportData?.clocktime || machineData.clocktime)
                .timeZone,
            }
          : undefined,
      location: reportData?.location || machineData.location,
      remoteBattery: reportData?.remoteBattery
        ? {
            ...reportData.remoteBattery,
            time: this.toDate(reportData.remoteBattery.time),
          }
        : undefined,
    } as Device;
  }

  private async searchDevice({
    field,
    value,
  }: {
    field: string;
    value: string;
  }): Promise<Device> {
    const machineSnapshot = await this.db
      .collection("machines")
      .where(field, "==", value)
      .limit(1)
      .get();

    if (machineSnapshot.empty) throw new Error("Device not found");

    const machineDoc = machineSnapshot.docs[0];
    return await this.buildDeviceFromDoc(machineDoc.id, machineDoc.data());
  }

  async getDeviceBySerialOrIdf(deviceSerialOrIdf: string): Promise<Device> {
    const clockDoc = await this.db
      .collection("machines")
      .doc(deviceSerialOrIdf)
      .get();

    if (clockDoc.exists) {
      return await this.buildDeviceFromDoc(clockDoc.id, clockDoc.data()!);
    }

    const lowercasedInput = deviceSerialOrIdf.toLowerCase();
    const idf =
      lowercasedInput.split("-").length === 3
        ? lowercasedInput
        : id.friendlyIdFromString(lowercasedInput);
    return await this.searchDevice({ field: "idf", value: idf });
  }

  public async getDevicesByIdfs(idfs: string[]): Promise<Device[]> {
    const machineSnapshot = await this.db
      .collection("machines")
      .where("idf", "in", idfs)
      .get();
    return machineSnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        registerProduct: data.registerProduct
          ? this.toDate(data.registerProduct.time)
          : undefined,
        autoCreate: data.autoCreate ?? false,
      } as Device;
    });
  }

  /**
   * Counts how many of the given idfs correspond to registered ("opened")
   * machines, i.e. machine docs whose `registerProduct.value === true`.
   *
   * Firestore `in` queries accept at most 30 values, so idfs are queried in
   * chunks of 30. Returns the number opened, the number of matching machine
   * docs found, and the total idfs requested.
   */
  public async countRegisteredByIdfs(idfs: string[]): Promise<{
    opened: number;
    found: number;
    total: number;
    clocks: { idf: string; registeredAt: Date | null }[];
  }> {
    const uniqueIdfs = Array.from(new Set(idfs));
    const chunkSize = 30;
    const chunks: string[][] = [];
    for (let i = 0; i < uniqueIdfs.length; i += chunkSize) {
      chunks.push(uniqueIdfs.slice(i, i + chunkSize));
    }

    let opened = 0;
    let found = 0;
    const clocks: { idf: string; registeredAt: Date | null }[] = [];
    await Promise.all(
      chunks.map(async (chunk) => {
        const snapshot = await this.db
          .collection("machines")
          .where("idf", "in", chunk)
          .get();
        snapshot.docs.forEach((doc) => {
          found += 1;
          const data = doc.data();
          if (data.registerProduct?.value === true) {
            opened += 1;
            clocks.push({
              idf: data.idf,
              registeredAt: data.registerProduct?.time
                ? this.toDate(data.registerProduct.time)
                : null,
            });
          }
        });
      }),
    );

    // Most recently registered first.
    clocks.sort(
      (a, b) =>
        (b.registeredAt?.getTime() ?? 0) - (a.registeredAt?.getTime() ?? 0),
    );

    return { opened, found, total: uniqueIdfs.length, clocks };
  }

  async getDeviceByEmail(email: string): Promise<Device> {
    return await this.searchDevice({
      field: "clientDetails.email",
      value: email,
    });
  }

  async findItem<T extends DataType>(
    handler: Collection,
    key: string,
    value: any,
    include: boolean = false,
  ) {
    const data = this.data[handler].data as T[];
    if (data)
      return data.find((item: T) =>
        include ? item[key].includes(value) : item[key] === value,
      );
    return (
      await this.db.collection(handler).where(key, "==", value).limit(1).get()
    ).docs[0]?.data() as T;
  }

  async getData<T extends DataType>(handler: Collection) {
    const dataArray = this.data[handler].data as T[];
    if (dataArray) return dataArray;

    return new Promise<T[]>((v) => {
      const l = (dataArr: T[]) => {
        this.removeListener(handler, l);
        v(dataArr);
      };
      this.listen<T>(handler, l);
    });
  }

  async fetchDocsByField<T extends DocumentData>(
    collection: string,
    field: string,
    operator: WhereFilterOp,
    value: T[keyof T],
  ): Promise<(T & { id: string })[]> {
    const querySnapshot = await this.db
      .collection(collection)
      .where(field, operator, value)
      .get();

    return querySnapshot.docs.map((doc) => {
      const x: T = doc.data() as T;
      return {
        id: doc.id,
        ...x,
      };
    });
  }
}

const instance = new FirebaseService();

export default instance;
